// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🎯 VIRTUAL TRAINING SESSION COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  startVirtualTrainingSession,
  getVirtualTrainingSession,
  completeVirtualTrainingSession,
  abandonVirtualTrainingSession,
} from '../../services/virtualTrainings';
import type { VirtualTrainingWithExercises, VirtualTrainingSessionWithDetails } from '../../types';
import { ExerciseSession } from './ExerciseSession';
import './VirtualTrainingSession.css';

interface VirtualTrainingSessionProps {
  training: VirtualTrainingWithExercises;
  onComplete: () => void;
  onCancel: () => void;
}

export const VirtualTrainingSession: React.FC<VirtualTrainingSessionProps> = ({
  training,
  onComplete,
  onCancel,
}) => {
  const { player } = useAuth();
  const { t } = useTranslation();

  const [sessionId, setSessionId] = useState<number | null>(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initSession = async () => {
    if (!player) return;

    try {
      const session = await startVirtualTrainingSession(player.id, training.id);
      setSessionId(session.id);
      setCurrentExerciseIndex(0);
      setCompletedCount(0);
    } catch (err) {
      console.error('Failed to start session:', err);
      alert(t('training.virtualTrainings.sessionStartFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleExerciseComplete = async () => {
    const newCompletedCount = completedCount + 1;
    setCompletedCount(newCompletedCount);

    // Move to next exercise or complete training
    if (currentExerciseIndex < sortedExercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    } else {
      // All exercises completed
      if (sessionId) {
        await completeVirtualTrainingSession(sessionId);
      }
      onComplete();
    }
  };

  const handleAbandon = async () => {
    if (!window.confirm(t('training.virtualTrainings.confirmAbandon'))) return;

    if (sessionId) {
      await abandonVirtualTrainingSession(sessionId);
    }
    onCancel();
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  const sortedExercises = [...(training.exercises || [])].sort((a, b) => a.order_index - b.order_index);
  const currentExerciseData = sortedExercises[currentExerciseIndex];
  const currentExercise = currentExerciseData?.exercise;

  if (!currentExercise) {
    return <div className="error">{t('training.virtualTrainings.noExercises')}</div>;
  }

  return (
    <div className="virtual-training-session">
      {/* Progress Header */}
      <div className="session-progress-header">
        <div className="training-title">
          <h2>🗓️ {training.title}</h2>
          <p>{training.description}</p>
        </div>
        <div className="progress-stats">
          <div className="progress-bar-container">
            <div className="progress-label">
              {t('training.virtualTrainings.progress')}: {completedCount} / {sortedExercises.length}
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(completedCount / sortedExercises.length) * 100}%` }}
              />
            </div>
          </div>
          <div className="exercise-counter">
            {t('training.virtualTrainings.currentExercise')}: {currentExerciseIndex + 1} / {sortedExercises.length}
          </div>
        </div>
      </div>

      {/* Current Exercise */}
      <ExerciseSession
        exercise={currentExercise}
        onComplete={handleExerciseComplete}
        onCancel={handleAbandon}
      />

      {/* Next Exercise Preview */}
      {currentExerciseIndex < sortedExercises.length - 1 && (
        <div className="next-exercise-preview">
          <h4>{t('training.virtualTrainings.nextExercise')}:</h4>
          <p>{sortedExercises[currentExerciseIndex + 1]?.exercise?.title}</p>
        </div>
      )}
    </div>
  );
};

